import {
  APIDateOnlyFieldAttributes,
  APIDateTimeFieldAttributes,
  APIFieldAttributes,
  APIFieldType,
  ArdoqId,
  FieldGlobalAttributes,
} from '@ardoq/api-types';
import type { I18nLocale } from '@ardoq/i18n';
import { getCurrentLocale } from '@ardoq/locale';
import { isDateFieldType } from '@ardoq/date-time';
import type {
  EnhancedScopeData,
  ModelEntityTypePair,
  TextAreaFormat,
} from '@ardoq/data-model';
import { dateRangeOperations } from '@ardoq/date-range';
import { getModelTypeDictionaryKey } from './internal/modelTypesUtils';
import { formatFieldValueToText } from './formatFieldValueToText';
import * as referenceUtils from './referenceUtils';
import { enhancedScopeDataOperations } from './enhancedScopeDataOperations';

export const getFieldData = (
  scopeData: EnhancedScopeData,
  fieldId: ArdoqId
) => {
  const field = scopeData.fieldsById[fieldId];
  return field ?? null;
};

// prettier-ignore
export function getCounterpartField(scopeData: EnhancedScopeData, field: APIDateOnlyFieldAttributes): APIDateOnlyFieldAttributes | null;
// prettier-ignore
export function getCounterpartField(scopeData: EnhancedScopeData, field: APIDateTimeFieldAttributes): APIDateTimeFieldAttributes | null;
// prettier-ignore
export function getCounterpartField(scopeData: EnhancedScopeData, field: APIDateOnlyFieldAttributes
                                                                       | APIDateTimeFieldAttributes): APIDateOnlyFieldAttributes
                                                                                                    | APIDateTimeFieldAttributes | null;
// prettier-ignore
export function getCounterpartField(scopeData: EnhancedScopeData, field: APIDateOnlyFieldAttributes
                                                                       | APIDateTimeFieldAttributes): APIDateOnlyFieldAttributes
                                                                                                    | APIDateTimeFieldAttributes | null {
  const isStartDate = dateRangeOperations.fieldNameIsDateRangeStartField(
    field.name
  );
  const isEndDate = dateRangeOperations.fieldNameIsDateRangeEndField(
    field.name
  );
  if (!isStartDate && !isEndDate) return null;

  const counterpartField = scopeData.fields.find(
    (candidateField) => dateRangeOperations.areMatchingDateTimeFields(field, candidateField)
  ) as APIDateOnlyFieldAttributes | APIDateTimeFieldAttributes | undefined;

  return counterpartField ?? null;
}

type ResolveFormattingDependenciesFromScopeDataArgs = {
  fieldAttributes: FieldGlobalAttributes;
  fieldType?: APIFieldType;
  value: any;
};
const getFormattingData = ({
  fieldAttributes,
  fieldType,
  value,
}: ResolveFormattingDependenciesFromScopeDataArgs) => {
  const locale = getCurrentLocale();

  if (!fieldAttributes) return null;

  const type = fieldType || fieldAttributes.type;

  if ((!isDateFieldType(type) && value === null) || value === undefined) {
    return null;
  }

  const numberFormatOption = fieldAttributes.numberFormatOptions;
  const fieldName = fieldAttributes.name;

  if (!type || !fieldName) {
    return null;
  }

  return { locale, type, numberFormatOption, fieldName };
};

export type FormatUserFieldFn = (val: unknown) => string | null;

type FieldAttributesToLabelArgs = {
  fieldAttributes: FieldGlobalAttributes;
  fieldType?: APIFieldType;
  value: any;
  i18nLocale: I18nLocale;
  formatUserFieldFn: FormatUserFieldFn;
  excludeTime?: boolean;
  textAreaFormat?: TextAreaFormat;
};
export const fieldAttributesToLabel = ({
  fieldAttributes,
  fieldType,
  value,
  i18nLocale,
  excludeTime,
  textAreaFormat,
  formatUserFieldFn,
}: FieldAttributesToLabelArgs) => {
  const formattingArgs = getFormattingData({
    fieldAttributes,
    fieldType,
    value,
  });

  if (!formattingArgs) {
    return null;
  }

  return formatFieldValueToText({
    ...formattingArgs,
    value,
    i18nLocale,
    excludeTime,
    textAreaFormat,
    formatUserField: formatUserFieldFn,
  });
};

/**
 * A temporary workaround for the fact that the scopeData does not account
 * for users that are referenced in custom fields
 */
const formatUserField = (val: any) => {
  if (typeof val !== 'string') return null;
  return val;
};
type FormatArgs = {
  scopeData: EnhancedScopeData;
  fieldId: ArdoqId;
  value: unknown;
  i18nLocale: I18nLocale;
  fieldType?: APIFieldType;
  excludeTime?: boolean;
  textAreaFormat?: TextAreaFormat;
  formatUserFieldFn?: FormatUserFieldFn;
};
export const format = ({
  scopeData,
  fieldId,
  value,
  i18nLocale,
  fieldType,
  excludeTime,
  textAreaFormat,
  formatUserFieldFn,
}: FormatArgs) => {
  // For date range fields, is better to pass the fieldType explicitly when possible. Because if you look up the
  // date range field id in the fields collection, it will return the start date field, which is a date time field.

  const fieldAttributes = getFieldData(scopeData, fieldId);

  if (!fieldAttributes) {
    return null;
  }

  return fieldAttributesToLabel({
    fieldAttributes,
    fieldType,
    value,
    i18nLocale,
    excludeTime,
    textAreaFormat,
    formatUserFieldFn: formatUserFieldFn ?? formatUserField,
  });
};

/**
 * Get label of custom field.
 * For "default" fields see DEFAULT_ATTRIBUTE_NAME_LABEL_MAP
 */
export const getLabel = (scopeData: EnhancedScopeData, fieldId: ArdoqId) => {
  const field = scopeData.fieldsById[fieldId];
  return field?.label || '';
};

/**
 * Get label of custom field
 * For "default" fields see DEFAULT_ATTRIBUTE_NAME_LABEL_MAP
 */
export const getLabelByFieldName = (
  scopeData: EnhancedScopeData,
  fieldName: string
): string | undefined => scopeData.fieldLabelByFieldName[fieldName];

export const appliesToComponent = (
  scopeData: EnhancedScopeData,
  fieldId: ArdoqId,
  componentId: ArdoqId
) => {
  const field = scopeData.fieldsById[fieldId];
  const component = scopeData.componentsById[componentId];
  if (!field || !component || field.model !== component.model) return false;
  const { typeId, model } = component;
  return field.name in getFieldsByType(scopeData, { typeId, modelId: model });
};

export const appliesToReference = (
  scopeData: EnhancedScopeData,
  fieldId: ArdoqId,
  referenceId: ArdoqId
) => {
  const field = scopeData.fieldsById[fieldId];
  const fieldReferenceTypeIds = field ? field.referenceType : [];
  const fieldModelId = field ? field.model : '';
  const isGlobal = field ? field.globalref : false;
  const reference = scopeData.referencesById[referenceId];
  const referenceTypeId = reference ? reference.type : '';
  const referenceModelId = reference ? reference.model : '';
  return field && reference
    ? fieldModelId === referenceModelId &&
        (isGlobal || fieldReferenceTypeIds.includes(`${referenceTypeId}`))
    : false;
};

/**
 * Returns a dictionary mapping field names to fields for a given component type
 */
export const getFieldsByType = (
  scopeData: EnhancedScopeData,
  { typeId, modelId }: ModelEntityTypePair
): Record<string, APIFieldAttributes> => {
  const key = getModelTypeDictionaryKey(modelId, typeId);
  return scopeData.fieldsByModelIdAndTypeId[key] || {};
};

export const getFieldsByReferenceType = (
  scopeData: EnhancedScopeData,
  modelTypePair: ModelEntityTypePair
): APIFieldAttributes[] => {
  const fields = getFieldsByType(scopeData, modelTypePair);
  return Object.values(fields);
};

export const getFieldsByComponentType = (
  scopeData: EnhancedScopeData,
  modelTypePair: ModelEntityTypePair
): APIFieldAttributes[] => {
  const fields = getFieldsByType(scopeData, modelTypePair);
  return Object.values(fields);
};

export const formatFieldValueForComponentOrReference = (
  scopeData: EnhancedScopeData,
  fieldId: ArdoqId,
  modelId: ArdoqId,
  i18nLocale: I18nLocale,
  formatUserFieldFn?: FormatUserFieldFn
) => {
  const fieldAttributes = scopeData.fieldsById[fieldId];
  if (!fieldAttributes?.name) {
    return null;
  }

  const fieldValue = getFieldValueForComponentOrReference(
    scopeData,
    modelId,
    fieldAttributes.name
  );

  return fieldAttributesToLabel({
    fieldAttributes,
    value: fieldValue,
    i18nLocale,
    excludeTime: true,
    textAreaFormat: 'text',
    formatUserFieldFn: formatUserFieldFn ?? formatUserField,
  });
};

export const getFieldValueForComponentOrReference = (
  scopeData: EnhancedScopeData,
  modelId: ArdoqId,
  fieldName: string
) => {
  if (referenceUtils.isReference(scopeData, modelId)) {
    return referenceUtils.getAttribute(scopeData, modelId, fieldName);
  }

  // duplicated componentUtils.getAttribute due to dependency cycle
  const component = enhancedScopeDataOperations.getComponent(
    scopeData,
    modelId
  );
  return component ? component[fieldName] : null;
};

export const getAllFieldsInWorkspace = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
) => {
  const workspace = scopeData.workspacesById[workspaceId];
  const model = scopeData.modelsById[workspace.componentModel];
  return (
    scopeData.fields.filter(field => field.model === model._id) ??
    ([] as APIFieldAttributes[])
  );
};
